import React, { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Calendar, Package, Image, Users, TrendingUp, Clock, CheckCircle, AlertCircle } from 'lucide-react';
import './Dashboard.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Dashboard = () => {
  const { stats, fetchStats } = useOutletContext();
  const [recentBookings, setRecentBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRecentBookings();
  }, []);

  const fetchRecentBookings = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_URL}/api/admin/bookings?limit=5`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setRecentBookings(data.slice(0, 5));
      }
    } catch (error) {
      console.error('Failed to fetch bookings:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    { 
      label: 'Total Bookings', 
      value: stats?.total_bookings || 0, 
      icon: Calendar, 
      color: '#6366f1',
      bg: '#eef2ff'
    },
    { 
      label: 'Pending', 
      value: stats?.pending_bookings || 0, 
      icon: Clock, 
      color: '#f59e0b',
      bg: '#fffbeb'
    },
    { 
      label: 'Completed', 
      value: stats?.completed_projects || 0, 
      icon: CheckCircle, 
      color: '#22c55e',
      bg: '#f0fdf4'
    },
    { 
      label: 'This Month', 
      value: stats?.this_month_bookings || 0, 
      icon: TrendingUp, 
      color: '#ec4899',
      bg: '#fdf2f8'
    },
    { 
      label: 'New Contacts', 
      value: stats?.new_contacts || 0, 
      icon: Users, 
      color: '#8b5cf6',
      bg: '#f5f3ff'
    },
  ];

  const getStatusBadge = (status) => {
    const styles = {
      pending: { bg: '#fef3c7', color: '#d97706' },
      confirmed: { bg: '#dbeafe', color: '#2563eb' },
      completed: { bg: '#dcfce7', color: '#16a34a' },
      cancelled: { bg: '#fee2e2', color: '#dc2626' }
    };
    return styles[status] || styles.pending;
  };

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <h1>Dashboard</h1>
        <p>Welcome back! Here's what's happening with your business.</p>
      </div>

      {/* Stats Grid */}
      <div className="stats-grid">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <div key={index} className="stat-card">
              <div className="stat-icon" style={{ background: stat.bg, color: stat.color }}>
                <Icon size={24} />
              </div>
              <div className="stat-info">
                <span className="stat-value">{stat.value}</span>
                <span className="stat-label">{stat.label}</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Recent Bookings */}
      <div className="dashboard-section">
        <div className="section-header">
          <h2>Recent Bookings</h2>
          <a href="/admin/bookings" className="view-all-link">View All →</a>
        </div>

        {loading ? (
          <div className="loading-state">Loading...</div>
        ) : recentBookings.length === 0 ? (
          <div className="empty-state">
            <Calendar size={48} />
            <p>No bookings yet</p>
          </div>
        ) : (
          <div className="bookings-table-wrapper">
            <table className="bookings-table">
              <thead>
                <tr>
                  <th>Client</th>
                  <th>Service</th>
                  <th>Date</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {recentBookings.map((booking) => {
                  const statusStyle = getStatusBadge(booking.status);
                  return (
                    <tr key={booking.id}>
                      <td>
                        <div className="client-info">
                          <span className="client-name">{booking.client_name}</span>
                          <span className="client-phone">{booking.client_phone}</span>
                        </div>
                      </td>
                      <td>{booking.service_name}</td>
                      <td>{new Date(booking.booking_date).toLocaleDateString('en-IN', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric'
                      })}</td>
                      <td>
                        <span 
                          className="status-badge"
                          style={{ background: statusStyle.bg, color: statusStyle.color }}
                        >
                          {booking.status}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div className="dashboard-section">
        <h2>Quick Actions</h2>
        <div className="quick-actions">
          <a href="/admin/bookings" className="action-card">
            <Calendar size={24} />
            <span>Manage Bookings</span>
          </a>
          <a href="/admin/services" className="action-card">
            <Package size={24} />
            <span>Edit Services</span>
          </a>
          <a href="/admin/portfolio" className="action-card">
            <Image size={24} />
            <span>Update Portfolio</span>
          </a>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
